/**
 * Analytics Settings Service — Backend-Synchronized GDPR Consent
 *
 * Provides functions to manage per-device analytics consent with:
 * - Backend synchronization via settings API
 * - Immutable audit logging (F4)
 * - Optimistic locking with version control (F6)
 * - Device pruning with audit log archival (F5, C1)
 * - Complete data deletion for right to erasure (F3)
 * - Migration from localStorage-only consent (M4)
 *
 * Security Controls: F3, F4, F5, F6, C1, M1, M2, M4
 * GDPR Articles: 5(1)(c), 6(1)(a), 7(1), 17, 25
 */

import type {
  AnalyticsConsent,
  ConsentAuditEntry,
  UserAnalyticsSettings,
  AuditLogArchiveEntry,
} from '../../utils/device/device-id';

// ============================================================================
// Error Classes (M2)
// ============================================================================

/**
 * Error thrown when consent update fails after retries.
 * UI should revert to previous state when this is caught.
 */
export class ConsentUpdateError extends Error {
  override readonly name = 'ConsentUpdateError';
  readonly originalCause?: Error;

  constructor(message: string, cause?: Error) {
    super(message);
    this.originalCause = cause;
  }
}

// ============================================================================
// Pure Building-Block Functions
// ============================================================================

/**
 * Create an audit log entry for a consent change (F4).
 *
 * @param action - What happened (consent_given or consent_withdrawn)
 * @param method - How the user triggered it
 * @param consentText - Optional consent text for GDPR Article 7 proof
 * @returns Immutable audit log entry with ISO 8601 timestamp
 */
export function createAuditLogEntry(
  action: ConsentAuditEntry['action'],
  method: ConsentAuditEntry['method'],
  consentText?: string
): ConsentAuditEntry {
  const entry: ConsentAuditEntry = {
    timestamp: new Date().toISOString(),
    action,
    method,
  };
  if (consentText !== undefined) {
    entry.consentText = consentText;
  }
  return entry;
}

/**
 * Build an updated UserAnalyticsSettings object with consent change.
 *
 * Pure function: does NOT mutate the input. Returns a new settings object.
 * Increments version (F6), appends audit log (F4), enforces F2 (no PII).
 *
 * @param existing - Current analytics settings from backend
 * @param deviceId - Device to update
 * @param enabled - New consent state
 * @param deviceLabel - Generic device label (F2: "Browser on OS")
 * @param method - How the user triggered the change
 * @param consentText - Optional consent text for GDPR Article 7 proof
 * @returns New settings object with updated consent
 */
export function buildAnalyticsSettingsUpdate(
  existing: UserAnalyticsSettings,
  deviceId: string,
  enabled: boolean,
  deviceLabel: string,
  method: ConsentAuditEntry['method'],
  consentText?: string
): UserAnalyticsSettings {
  const now = new Date().toISOString();
  const action: ConsentAuditEntry['action'] = enabled ? 'consent_given' : 'consent_withdrawn';
  const auditEntry = createAuditLogEntry(action, method, consentText);

  const existingDevice = existing.devices[deviceId];
  const existingAuditLog = existingDevice?.auditLog ?? [];

  const updatedDevice: AnalyticsConsent = {
    enabled,
    consentDate: now,
    deviceId,
    deviceLabel,
    lastSeen: now,
    auditLog: [...existingAuditLog, auditEntry],
  };

  return {
    ...existing,
    version: existing.version + 1,
    devices: {
      ...existing.devices,
      [deviceId]: updatedDevice,
    },
  };
}

/**
 * Delete ALL device consent data (F3: Right to erasure).
 *
 * Returns new settings with empty devices and incremented version.
 * Used during account deletion flow.
 *
 * @param settings - Current analytics settings
 * @returns New settings with all devices removed
 */
export function deleteAllDeviceConsent(
  settings: UserAnalyticsSettings
): UserAnalyticsSettings {
  return {
    ...settings,
    version: settings.version + 1,
    devices: {},
  };
}

/**
 * Archive a device's audit log before pruning (C1).
 *
 * MUST be called BEFORE removing a device from the devices map.
 * Archived logs are retained for 3 years per GDPR Article 7(1).
 *
 * @param settings - Current analytics settings
 * @param deviceId - Device whose audit log to archive
 * @returns New settings with audit log archived
 */
export function archiveDeviceAuditLog(
  settings: UserAnalyticsSettings,
  deviceId: string
): UserAnalyticsSettings {
  const device = settings.devices[deviceId];
  if (!device) return settings;

  const archiveEntry: AuditLogArchiveEntry = {
    deviceId,
    deviceLabel: device.deviceLabel,
    archivedAt: new Date().toISOString(),
    archivedReason: 'inactive_device_pruned_30_days',
    auditLog: [...device.auditLog],
    consentDate: device.consentDate,
    lastSeen: device.lastSeen,
  };

  const existingArchive = settings.auditLogArchive ?? [];

  return {
    ...settings,
    auditLogArchive: [...existingArchive, archiveEntry],
  };
}

/**
 * Prune devices inactive for longer than retentionDays (F5).
 *
 * C1 CRITICAL: Archives audit logs BEFORE deleting devices.
 * Increments version if any devices were pruned.
 *
 * @param settings - Current analytics settings
 * @param retentionDays - Days of inactivity before pruning (default: 30)
 * @returns New settings with inactive devices pruned and audit logs archived
 */
export function pruneInactiveDevices(
  settings: UserAnalyticsSettings,
  retentionDays: number = 30
): UserAnalyticsSettings {
  const cutoffDate = new Date();
  cutoffDate.setDate(cutoffDate.getDate() - retentionDays);

  const deviceIds = Object.keys(settings.devices);
  const inactiveIds: string[] = [];

  for (const id of deviceIds) {
    const device = settings.devices[id];
    const lastSeen = new Date(device.lastSeen);
    if (lastSeen < cutoffDate) {
      inactiveIds.push(id);
    }
  }

  if (inactiveIds.length === 0) {
    return settings;
  }

  // C1: Archive audit logs BEFORE pruning
  let result = { ...settings };
  for (const id of inactiveIds) {
    result = archiveDeviceAuditLog(result, id);
  }

  // Remove inactive devices
  const remainingDevices = { ...result.devices };
  for (const id of inactiveIds) {
    delete remainingDevices[id];
  }

  return {
    ...result,
    version: result.version + 1,
    devices: remainingDevices,
  };
}

/**
 * Check if a device has current consent in the backend settings.
 *
 * @param settings - Current analytics settings
 * @param deviceId - Device to check
 * @returns true if the device exists and has consent enabled
 */
export function hasDeviceConsent(
  settings: UserAnalyticsSettings,
  deviceId: string
): boolean {
  return settings.devices[deviceId]?.enabled === true;
}

/**
 * Update a device's lastSeen timestamp.
 *
 * Called on app startup to keep retention policy accurate (F5).
 *
 * @param settings - Current analytics settings
 * @param deviceId - Device to update
 * @returns New settings with updated lastSeen, or unchanged if device not found
 */
export function updateDeviceLastSeen(
  settings: UserAnalyticsSettings,
  deviceId: string
): UserAnalyticsSettings {
  const device = settings.devices[deviceId];
  if (!device) return settings;

  return {
    ...settings,
    devices: {
      ...settings.devices,
      [deviceId]: {
        ...device,
        lastSeen: new Date().toISOString(),
      },
    },
  };
}

// ============================================================================
// Migration (M4)
// ============================================================================

/**
 * Migration decision result.
 */
export interface MigrationResult {
  shouldMigrate: boolean;
  consentValue: boolean;
}

/**
 * Determine whether localStorage consent should be migrated to backend (M4).
 *
 * Migration is skipped if the backend already has ANY device consent records
 * to avoid conflicts.
 *
 * @param localStorageConsent - Value from localStorage (e.g., 'true' or 'false'), or null if unset
 * @param backendSettings - Current analytics settings from backend
 * @returns Migration payload if migration should occur, null otherwise
 */
export function migrateLocalStorageConsent(
  localStorageConsent: string | null,
  backendSettings: UserAnalyticsSettings
): MigrationResult | null {
  // No localStorage consent to migrate
  if (localStorageConsent === null) return null;

  // Backend already has device consent — skip migration (M4 conflict avoidance)
  const existingDevices = Object.keys(backendSettings.devices);
  if (existingDevices.length > 0) return null;

  return {
    shouldMigrate: true,
    consentValue: localStorageConsent === 'true',
  };
}

// ============================================================================
// Backend-Interacting Functions (stubs — to be wired to settings API)
// ============================================================================

/**
 * Fetch analytics consent state from backend.
 *
 * @param _userId - User ID (used for backend query)
 * @returns Current analytics settings from backend, or default empty settings
 */
export async function getAnalyticsConsentFromBackend(
  _userId: string
): Promise<UserAnalyticsSettings> {
  // TODO: Wire to loadUserSettings() -> extract analytics section
  return { version: 0, devices: {} };
}

/**
 * Save analytics consent to backend with optimistic locking (F6).
 *
 * @param _userId - User ID
 * @param _settings - Updated analytics settings to save
 * @throws ConsentUpdateError if retries exhausted (M2)
 */
export async function setAnalyticsConsentToBackend(
  _userId: string,
  _settings: UserAnalyticsSettings
): Promise<void> {
  // TODO: Wire to settings API with version check + retry
}
